import React, { useContext, useState, useRef, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, Drawer, Divider, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { OpenInNewOutlined, WarningAmberOutlined, MenuOutlined, CloseOutlined, AddOutlined, ImageOutlined } from '@mui/icons-material';
import { PRIMARY } from '../constants';
import { PreviewCtx } from '../contexts';
import { ContentBrowserModal } from '../../lobbyBuilderTab/ContentBrowserModal';

function uid() { return Math.random().toString(36).slice(2, 9); }
const isUrl = str => /^https?:\/\//i.test((str || '').trim()) || /^www\./i.test((str || '').trim());


// ─── HeaderLogoPopup ──────────────────────────────────────────────────────────
function HeaderLogoPopup({ anchorEl, config, onChangeConfig, onClose }) {
    const [pos, setPos] = useState(null);
    const [contentBrowserOpen, setContentBrowserOpen] = useState(false);
    const popRef = useRef(null);

    useLayoutEffect(() => {
        if (!anchorEl) return;
        const r = anchorEl.getBoundingClientRect();
        setPos({ top: r.bottom + 8, left: r.left });
    }, [anchorEl]);

    useEffect(() => {
        if (!anchorEl) return;
        const handler = (e) => {
            if (contentBrowserOpen) return;
            if (popRef.current && !popRef.current.contains(e.target) && anchorEl && !anchorEl.contains(e.target)) onClose();
        };
        const t = setTimeout(() => document.addEventListener('mousedown', handler), 0);
        return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
    }, [anchorEl, onClose, contentBrowserOpen]);

    if (!pos) return null;
    const upd = (patch) => onChangeConfig({ ...config, ...patch });

    return ReactDOM.createPortal(
        <>
            <Box ref={popRef} onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}
                sx={{ position: 'fixed', top: pos.top, left: pos.left, width: 240, background: '#fff', border: '1px solid #e4e9f0', borderRadius: '12px', boxShadow: '0 8px 28px rgba(0,0,0,0.14)', p: 1.5, zIndex: 100, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Logo Image</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {config.logoSrc ? (
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: '7px', background: '#f5f7fa', border: '1px solid #e4e9f0', overflow: 'hidden' }}>
                            <Box component="img" src={config.logoSrc} sx={{ height: 22, width: 'auto', objectFit: 'contain', maxWidth: 120 }} />
                            <Box onClick={() => upd({ logoSrc: '' })} sx={{ ml: 'auto', cursor: 'pointer', color: '#c4cdd8', fontSize: 12, '&:hover': { color: '#dc2626' } }}>✕</Box>
                        </Box>
                    ) : (
                        <Box onClick={() => setContentBrowserOpen(true)}
                            sx={{ flex: 1, py: 0.65, borderRadius: '7px', border: '1px dashed #d4dbe6', background: '#f8f9fb', cursor: 'pointer', fontSize: '0.72rem', color: '#9ea8b3', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, '&:hover': { borderColor: PRIMARY, color: PRIMARY } }}>
                            <ImageOutlined sx={{ fontSize: 14 }} /> Choose image…
                        </Box>
                    )}
                    {config.logoSrc && (
                        <Box onClick={() => setContentBrowserOpen(true)} sx={{ px: 0.75, py: 0.5, borderRadius: '6px', border: '1px solid #e4e9f0', cursor: 'pointer', fontSize: '0.65rem', color: '#5a6474', '&:hover': { background: '#f0f4f8' } }}>Change</Box>
                    )}
                </Box>
                <Box sx={{ borderTop: '1px solid #f0f2f5' }} />
                <Box>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>Logo Text</Typography>
                    <Box sx={{ border: '1.5px solid #e4e9f0', borderRadius: '7px', px: 1, py: 0.5, background: '#f8f9fb', '&:focus-within': { borderColor: PRIMARY, background: '#fff' } }}>
                        <Box component="input" value={config.logoText || ''} onChange={e => upd({ logoText: e.target.value })} placeholder="Event Name"
                            sx={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: '0.78rem', fontFamily: 'inherit', color: '#1a1a2e' }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.6rem', color: '#b0bac8', mt: 0.4 }}>Shown when no logo image is set</Typography>
                </Box>
            </Box>
            {contentBrowserOpen && ReactDOM.createPortal(
                <Box sx={{ inset: 0, zIndex: 999999 }}>
                    <ContentBrowserModal open onClose={() => setContentBrowserOpen(false)} mediaType="image"
                        onSelect={url => { upd({ logoSrc: url }); setContentBrowserOpen(false); }} />
                </Box>, document.body
            )}
        </>, document.body
    );
}

// ─── LoginRedirectPicker ──────────────────────────────────────────────────────
function LoginRedirectPicker({ allPages = [], allForms = [], allLobbies = [], currentHref, buildHref, onChange }) {
    const [q, setQ] = useState('');
    const looksLikeUrl = isUrl(q);
    const filtered = [
        ...allPages.map(p => ({ id: p._id, label: p.title, href: buildHref(p.slug), icon: '📄' })),
        ...allForms.map(f => ({ id: f._id, label: f.name, href: buildHref(f.slug), icon: '📝' })),
        ...allLobbies.map(l => ({ id: l._id, label: l.name, href: buildHref(l.slug), icon: '🎪' })),
    ].filter(item => !q || item.label?.toLowerCase().includes(q.toLowerCase()));

    return (
        <div style={{ background: '#f0f4f8', borderRadius: 8, padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* Current value chip */}
            {currentHref && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: alpha(PRIMARY, 0.07), border: `1px solid ${alpha(PRIMARY, 0.2)}`, borderRadius: 6, padding: '4px 8px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: PRIMARY, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        ↪ {currentHref}
                    </div>
                    <div onClick={() => onChange(null)} style={{ cursor: 'pointer', color: '#9ea8b3', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>✕</div>
                </div>
            )}
            {/* Search */}
            <div style={{ position: 'relative' }}>
                <input
                    value={q} onChange={e => setQ(e.target.value)}
                    placeholder={currentHref ? 'Change redirect…' : 'Search or paste URL…'}
                    style={{ width: '100%', boxSizing: 'border-box', border: `1.5px solid ${looksLikeUrl ? '#2d9e4e' : '#e4e9f0'}`, borderRadius: 6, padding: '5px 8px', paddingRight: looksLikeUrl ? 64 : 8, fontSize: 11.5, outline: 'none', background: '#fff', fontFamily: 'inherit' }}
                />
                {looksLikeUrl && (
                    <div onClick={() => { onChange(q.trim()); setQ(''); }}
                        style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', background: '#2d9e4e', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, cursor: 'pointer' }}>
                        Use URL
                    </div>
                )}
            </div>
            {/* Results */}
            {!looksLikeUrl && q && (
                <div style={{ maxHeight: 120, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {filtered.length === 0
                        ? <div style={{ fontSize: 11, color: '#c0c8d4', fontStyle: 'italic', padding: '2px 4px' }}>No results</div>
                        : filtered.map(item => {
                            const isSel = (currentHref || '') === item.href;
                            return (
                                <div key={item.id} onClick={() => { onChange(item.href); setQ(''); }}
                                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px', borderRadius: 6, cursor: 'pointer', background: isSel ? alpha(PRIMARY, 0.07) : '#fff', border: `1px solid ${isSel ? PRIMARY : 'transparent'}` }}
                                    onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = '#f0f4f8'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = isSel ? alpha(PRIMARY, 0.07) : '#fff'; }}>
                                    <span style={{ fontSize: 12 }}>{item.icon}</span>
                                    <span style={{ fontSize: 11.5, fontWeight: isSel ? 700 : 500, color: isSel ? PRIMARY : '#2c3444', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
                                    {isSel && <span style={{ color: PRIMARY, fontSize: 11 }}>✓</span>}
                                </div>
                            );
                        })
                    }
                </div>
            )}
        </div>
    );
}

// ─── PageLinkPickerPopup ──────────────────────────────────────────────────────
export function PageLinkPickerPopup({ anchorEl, allPages = [], allForms = [], allLobbies = [], allSections = [], currentHref, onSelect, onSelectExternal, onClose, showLogin, onToggleLogin, loginRedirectHref, onLoginRedirectChange, isLoginPicker = false, eventSlug = '', eventUrlMode = 'prefixed', currentPageSlug = '' }) {
    const [query, setQuery] = useState('');
    const [pos,   setPos]   = useState(null);
    const popRef = useRef(null);
    const buildHref = (slug) => eventUrlMode === 'flat' ? `/${slug}` : eventSlug ? `/${eventSlug}/${slug}` : `/${slug}`;

    useLayoutEffect(() => {
        if (!anchorEl) return;
        const r = anchorEl.getBoundingClientRect();
        setPos({ top: r.bottom + 6, left: Math.max(4, Math.min(r.left, window.innerWidth - 308)) });
    }, [anchorEl]);

    useEffect(() => {
        const handler = e => { if (popRef.current && !popRef.current.contains(e.target) && anchorEl && !anchorEl.contains(e.target)) onClose(); };
        const t = setTimeout(() => document.addEventListener('mousedown', handler), 0);
        return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
    }, [onClose, anchorEl]);

    if (!pos) return null;
    const q = query.toLowerCase().trim();
    const looksLikeUrl = isUrl(query);

    const SECTIONS = [
        { key: 'sections', label: 'Sections on this page', items: allSections.filter(s => !q || (s.label || s.type)?.toLowerCase().includes(q)).map(s => { const sectionHref = buildHref(currentPageSlug) + `#section-${s.id}`; return { id: s.id, label: s.label || s.type || 'Section', href: sectionHref, type: 'section', thumb: null }; }) },
        { key: 'pages',   label: 'Pages',         items: allPages.filter(p => p.slug !== currentPageSlug && (!q || p.title?.toLowerCase().includes(q))).map(p => ({ id: p._id, label: p.title, href: buildHref(p.slug), type: 'page', thumb: null })) },
        { key: 'forms',   label: 'Forms',          items: allForms.filter(f => !q || f.name?.toLowerCase().includes(q)).map(f => ({ id: f._id, label: f.name, href: buildHref(f.slug), type: 'form', thumb: null })) },
        { key: 'lobbies', label: 'Virtual Events', items: allLobbies.filter(l => !q || l.name?.toLowerCase().includes(q)).map(l => ({ id: l._id, label: l.name, href: buildHref(l.slug), type: 'lobby', thumb: l.bgUrl || null })) },
    ];

    return ReactDOM.createPortal(
        <div ref={popRef} onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: 300, maxHeight: 400, background: '#fff', border: '1px solid #e4e9f0', borderRadius: 12, boxShadow: '0 10px 36px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 99999 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px 7px', borderBottom: '1px solid #f0f4f8', flexShrink: 0 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e' }}>Link destination</span>
                <div onClick={onClose} style={{ width: 20, height: 20, borderRadius: '50%', background: '#f0f4f8', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 11, color: '#9ea8b3' }}>✕</div>
            </div>
            {onToggleLogin !== undefined && (
                <div style={{ borderBottom: '1px solid #f0f4f8', flexShrink: 0, background: '#fafbfc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px' }}>
                        <div>
                            <div style={{ fontSize: 11.5, fontWeight: 600, color: '#1a1a2e' }}>Show Login button</div>
                            <div style={{ fontSize: 10.5, color: '#9ea8b3', marginTop: 1 }}>Display alongside the CTA button</div>
                        </div>
                        <div onClick={() => onToggleLogin(!showLogin)}
                            style={{ width: 36, height: 20, borderRadius: 10, background: showLogin ? PRIMARY : '#d1d9e0', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0, marginLeft: 8 }}>
                            <div style={{ position: 'absolute', top: 2, left: showLogin ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s' }} />
                        </div>
                    </div>
                    {showLogin && onLoginRedirectChange !== undefined && (
                        <div style={{ padding: '0 12px 10px' }}>
                            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>Redirect after login</div>
                            <LoginRedirectPicker
                                allPages={allPages} allForms={allForms} allLobbies={allLobbies}
                                currentHref={loginRedirectHref}
                                buildHref={buildHref}
                                onChange={onLoginRedirectChange}
                            />
                        </div>
                    )}
                </div>
            )}
            {(!isLoginPicker || showLogin) && (
                <div style={{ padding: '8px 10px', borderBottom: '1px solid #f0f4f8', flexShrink: 0 }}>
                    {isLoginPicker && (
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>
                            Where the user goes after login
                        </div>
                    )}
                    <div style={{ position: 'relative' }}>
                        <input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Search or paste a URL…"
                            style={{ width: '100%', boxSizing: 'border-box', border: `1.5px solid ${looksLikeUrl ? '#2d9e4e' : '#e4e9f0'}`, borderRadius: 7, padding: '6px 10px', paddingRight: looksLikeUrl ? 68 : 10, fontSize: 12.5, outline: 'none', background: '#f8f9fb', fontFamily: 'inherit' }} />
                        {looksLikeUrl && (
                            <div onClick={() => { onSelectExternal(query.trim()); onClose(); }}
                                style={{ position: 'absolute', right: 5, top: '50%', transform: 'translateY(-50%)', background: '#2d9e4e', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 5, cursor: 'pointer' }}>Use URL</div>
                        )}
                    </div>
                </div>
            )}
            <div style={{ flex: 1, overflowY: 'auto', padding: '6px', display: (!isLoginPicker || showLogin) ? undefined : 'none' }}>
                {!looksLikeUrl && SECTIONS.map((sec, si) => (
                    <div key={sec.key} style={{ marginBottom: si < SECTIONS.length - 1 ? 12 : 0 }}>
                        <div style={{ fontSize: 10.5, fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '2px 6px 6px' }}>{sec.label}</div>
                        {sec.items.length === 0
                            ? <div style={{ fontSize: 12, color: '#c0c8d4', fontStyle: 'italic', padding: '2px 6px 4px' }}>No {sec.label.toLowerCase()} found</div>
                            : sec.items.map(item => {
                                const isSel = (currentHref || '').replace(/^\//, '') === (item.href || '').replace(/^\//, '');
                                return (
                                    <div key={item.id} onClick={() => { onSelect(item); onClose(); }}
                                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', borderRadius: 8, cursor: 'pointer', marginBottom: 3, background: isSel ? alpha(PRIMARY, 0.07) : 'transparent', border: `1px solid ${isSel ? PRIMARY : 'transparent'}`, transition: 'all 0.12s' }}
                                        onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = '#f5f7fa'; }}
                                        onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = isSel ? alpha(PRIMARY, 0.07) : 'transparent'; }}>
                                        {item.thumb !== undefined && (
                                            <div style={{ width: 36, height: 24, borderRadius: 5, flexShrink: 0, background: item.thumb ? `url(${item.thumb}) center/cover` : 'linear-gradient(135deg,#1a2540,#2d4a6b)', border: '1px solid #e4e9f0' }} />
                                        )}
                                        <div style={{ fontSize: 12.5, fontWeight: isSel ? 700 : 500, color: isSel ? PRIMARY : '#2c3444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{item.label}</div>
                                        {isSel && <div style={{ fontSize: 12, color: PRIMARY }}>✓</div>}
                                    </div>
                                );
                            })
                        }
                    </div>
                ))}
            </div>
        </div>,
        document.body
    );
}

// ─── HeaderEditableNavLink ────────────────────────────────────────────────────
function HeaderEditableNavLink({ link, allPages, allForms, allLobbies, allSections, linkColor, onChange, onDelete, eventSlug, eventUrlMode, currentPageSlug }) {
    const [pickerOpen, setPickerOpen] = useState(false);
    const [hovered,    setHovered]    = useState(false);
    const [editing,    setEditing]    = useState(false);
    const [draft,      setDraft]      = useState(link.label || '');
    const anchorRef = useRef(null);
    const inputRef  = useRef(null);

    useEffect(() => { setDraft(link.label || ''); }, [link.label]);
    useEffect(() => { if (editing && inputRef.current) inputRef.current.select(); }, [editing]);

    const commitLabel = () => {
        setEditing(false);
        const t = draft.trim();
        if (t && t !== link.label) onChange({ ...link, label: t });
        else setDraft(link.label || '');
    };

    return (
        <>
            <Box ref={anchorRef} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
                sx={{ position: 'relative', display: 'flex', alignItems: 'center', px: 0.75, py: 0.4, borderRadius: '6px', background: pickerOpen ? alpha(PRIMARY, 0.08) : hovered ? alpha(PRIMARY, 0.05) : 'transparent', border: `1px solid ${pickerOpen ? PRIMARY : hovered ? alpha(PRIMARY, 0.25) : 'transparent'}`, transition: 'all 0.15s' }}>
                {editing ? (
                    <Box component="input" ref={inputRef} value={draft} onChange={e => setDraft(e.target.value)}
                        onBlur={commitLabel}
                        onKeyDown={e => { if (e.key === 'Enter') commitLabel(); if (e.key === 'Escape') { setDraft(link.label || ''); setEditing(false); } e.stopPropagation(); }}
                        onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}
                        sx={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '0.875rem', fontWeight: 500, fontFamily: 'inherit', color: linkColor || '#1a1a2e', width: `${Math.max(draft.length, 4)}ch`, minWidth: 40, maxWidth: 140, p: 0 }} />
                ) : (
                    <Typography onClick={e => { e.stopPropagation(); setPickerOpen(v => !v); }}
                        onDoubleClick={e => { e.stopPropagation(); setPickerOpen(false); setEditing(true); }}
                        title="Click to change destination · Double-click to rename"
                        sx={{ fontSize: '0.875rem', fontWeight: 500, color: linkColor || '#1a1a2e', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                        {link.label || 'Link'}
                    </Typography>
                )}
                {hovered && !editing && (
                    <Box onClick={e => { e.stopPropagation(); onDelete(); }}
                        sx={{ position: 'absolute', top: -6, right: -6, width: 14, height: 14, borderRadius: '50%', background: '#dc2626', color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5, cursor: 'pointer', '&:hover': { background: '#b91c1c' } }}>✕</Box>
                )}
            </Box>
            {pickerOpen && (
                <PageLinkPickerPopup anchorEl={anchorRef.current} allPages={allPages} allForms={allForms} allLobbies={allLobbies} allSections={allSections}
                    currentHref={link.href} eventSlug={eventSlug} eventUrlMode={eventUrlMode} currentPageSlug={currentPageSlug}
                    onSelect={item => { 
                        console.log('Selected item:', item);
                        onChange({ ...link, href: item.href, type: item.type, label: link.label || item.label }); 
                        setPickerOpen(false); 
                    }}
                    onSelectExternal={href => { onChange({ ...link, href, type: 'external', label: link.label || href }); setPickerOpen(false); }}
                    onClose={() => setPickerOpen(false)} />
            )}
        </>
    );
}

// ─── HeaderEditableNavLinksArea ───────────────────────────────────────────────
function HeaderEditableNavLinksArea({ config, allPages, allForms, allLobbies, allSections, onChange, eventSlug, eventUrlMode, currentPageSlug }) {
    const [addPickerOpen, setAddPickerOpen] = useState(false);
    const addBtnRef = useRef(null);
    const links = config.links || [];
    const updateLink = (i, p) => onChange([...links.slice(0, i), p, ...links.slice(i + 1)]);
    const deleteLink = (i)    => onChange(links.filter((_, idx) => idx !== i));

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            {links.length === 0 && <Typography sx={{ fontSize: '0.75rem', color: '#c0c8d4', fontStyle: 'italic', mr: 0.5 }}>No nav links</Typography>}
            {links.map((link, i) => (
                <HeaderEditableNavLink key={link.id || i} link={link} allPages={allPages} allForms={allForms} allLobbies={allLobbies} allSections={allSections}
                    linkColor={config.style?.linkColor} onChange={p => updateLink(i, p)} onDelete={() => deleteLink(i)} eventSlug={eventSlug} eventUrlMode={eventUrlMode} currentPageSlug={currentPageSlug} />
            ))}
            <Box ref={addBtnRef} onClick={e => { e.stopPropagation(); setAddPickerOpen(v => !v); }}
                sx={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1.5px dashed ${addPickerOpen ? PRIMARY : '#c4cdd8'}`, color: addPickerOpen ? PRIMARY : '#c4cdd8', cursor: 'pointer', transition: 'all 0.15s', '&:hover': { borderColor: PRIMARY, color: PRIMARY, background: alpha(PRIMARY, 0.06) } }}>
                <AddOutlined sx={{ fontSize: 13 }} />
            </Box>
            {addPickerOpen && (
                <PageLinkPickerPopup anchorEl={addBtnRef.current} allPages={allPages} allForms={allForms} allLobbies={allLobbies} allSections={allSections} currentHref=""
                    eventSlug={eventSlug} eventUrlMode={eventUrlMode} currentPageSlug={currentPageSlug}
                    onSelect={item => { onChange([...links, { id: uid(), label: item.label, href: item.href, type: item.type }]); setAddPickerOpen(false); }}
                    onSelectExternal={href => { onChange([...links, { id: uid(), label: href, href, type: 'external' }]); setAddPickerOpen(false); }}
                    onClose={() => setAddPickerOpen(false)} />
            )}
        </Box>
    );
}

// ─── External link confirmation dialog ───────────────────────────────────────
function ExternalLinkDialog({ url, open, onConfirm, onCancel }) {
    return (
        <Dialog open={open} onClose={onCancel} onClick={e => e.stopPropagation()}
            PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 24px 64px rgba(0,0,0,0.18)', maxWidth: 400, width: '100%', p: 0.5 } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 0.5 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: 2, background: alpha('#f59e0b', 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <WarningAmberOutlined sx={{ fontSize: 18, color: '#f59e0b' }} />
                </Box>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a2e' }}>Visit external link?</Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 0.5 }}>
                <Typography sx={{ fontSize: '0.82rem', color: '#5a6474', mb: 1 }}>You're about to leave this page and visit:</Typography>
                <Box sx={{ px: 1.5, py: 1, background: '#f0f4f8', borderRadius: 1.5, border: '1px solid #e4e9f0', wordBreak: 'break-all' }}>
                    <Typography sx={{ fontSize: '0.78rem', fontFamily: 'monospace', color: '#1075BA' }}>{url}</Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, pb: 2 }}>
                <Button onClick={onCancel} variant="outlined" size="small"
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: '#e4e9f0', color: '#5a6474', '&:hover': { borderColor: '#c4cdd8', background: '#f8f9fb' } }}>
                    Cancel
                </Button>
                <Button onClick={onConfirm} variant="contained" size="small" startIcon={<OpenInNewOutlined sx={{ fontSize: 14 }} />}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, background: PRIMARY, boxShadow: 'none', '&:hover': { background: '#0d62a0', boxShadow: 'none' } }}>
                    Visit link
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ─── Nav link — works in both builder and preview ─────────────────────────────
function NavLink({ link, color, onExternalConfirm, mobile = false }) {
    const type = link.type || 'external';
    const href = link.href || '';

    const handleClick = (e) => {
        e.stopPropagation();
        if (type === 'section') {
            const sectionId = href.replace('#section-', '');
            const el = document.getElementById(`section-${sectionId}`) || document.querySelector(`[data-section-id="${sectionId}"]`);
            if (el) {
                let scrollParent = el.parentElement;
                while (scrollParent && scrollParent !== document.body) {
                    const { overflow, overflowY } = window.getComputedStyle(scrollParent);
                    if (/auto|scroll/.test(overflow) || /auto|scroll/.test(overflowY)) break;
                    scrollParent = scrollParent.parentElement;
                }
                if (scrollParent && scrollParent !== document.body) {
                    const parentRect = scrollParent.getBoundingClientRect();
                    const elRect = el.getBoundingClientRect();
                    scrollParent.scrollBy({ top: elRect.top - parentRect.top - 16, behavior: 'smooth' });
                } else {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
            return;
        }
        if (type === 'page') { if (href) window.location.href = href; return; }
        if (href) onExternalConfirm(href, !!link.newTab);
    };

    return (
        <Typography onClick={handleClick} sx={{
            fontSize: mobile ? '1rem' : '0.875rem',
            fontWeight: mobile ? 500 : 500,
            color: color || '#1a1a2e',
            cursor: 'pointer',
            whiteSpace: mobile ? 'normal' : 'nowrap',
            transition: 'color 0.15s',
            py: mobile ? 0.5 : 0,
            '&:hover': { color: PRIMARY, textDecoration: 'underline' },
        }}>
            {link.label || 'Link'}
        </Typography>
    );
}

// ─── Logo ─────────────────────────────────────────────────────────────────────
function LogoArea({ config, onLogoClick }) {
    const clickSx = onLogoClick ? { cursor: 'pointer', outlineOffset: 3, borderRadius: 1, '&:hover': { outline: `1.5px solid ${PRIMARY}` } } : {};
    if (config.logoSrc) {
        return (
            <Box onClick={onLogoClick} sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ...clickSx }}>
                <Box component="img" src={config.logoSrc} alt={config.logoAlt || 'Logo'}
                    sx={{ height: config.logoHeight || 36, width: 'auto', display: 'block', objectFit: 'contain' }} />
            </Box>
        );
    }
    return (
        <Box onClick={onLogoClick} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, ...clickSx }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '50%', background: alpha(PRIMARY, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ width: 14, height: 14, borderRadius: '50%', background: PRIMARY }} />
            </Box>
            <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: PRIMARY, letterSpacing: '-0.5px' }}>
                {config.logoText || 'Event'}
            </Typography>
        </Box>
    );
}

// ─── Mobile side drawer ───────────────────────────────────────────────────────
function MobileDrawer({ open, onClose, config, onExternalConfirm, isBuilderMode }) {
    const links = config.links || [];

    // In builder mode we use a plain overlay box instead of MUI Drawer
    // because Drawer renders in a portal outside the canvas and may look odd.
    // We simulate it with an absolutely-positioned panel.
    if (isBuilderMode) {
        if (!open) return null;
        return (
            <Box
                onClick={e => e.stopPropagation()}
                sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: 240,
                    //   height: '100vh',
                    maxHeight: 600,
                    background: '#fff',
                    boxShadow: '-4px 0 24px rgba(0,0,0,0.14)',
                    zIndex: 999,
                    display: 'flex',
                    flexDirection: 'column',
                    borderLeft: '1px solid #e8edf2',
                }}
            >
                {/* Drawer header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2, borderBottom: '1px solid #f0f2f5' }}>
                    <LogoArea config={config} />
                    <IconButton size="small" onClick={onClose} sx={{ color: '#5a6474' }}>
                        <CloseOutlined sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>

                {/* Nav links */}
                <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {links.length === 0 ? (
                        <Typography sx={{ fontSize: '0.8rem', color: '#c0c8d4', fontStyle: 'italic' }}>No nav links added</Typography>
                    ) : (
                        links.map((link, i) => (
                            <NavLink key={link.id || i} link={link} color={config.style?.linkColor} onExternalConfirm={onExternalConfirm} mobile />
                        ))
                    )}
                </Box>

                {/* Auth buttons */}
                <Box sx={{ px: 2.5, py: 2.5, borderTop: '1px solid #f0f2f5', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: config.style?.loginColor || PRIMARY, cursor: 'pointer', textAlign: 'center' }}>
                        {config.loginLabel || 'Login'}
                    </Typography>
                    <Box sx={{ py: 1.25, borderRadius: '8px', background: config.style?.registerBg || PRIMARY, cursor: 'pointer', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: config.style?.registerColor || '#fff' }}>
                            {config.registerLabel || 'Register Now'}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    }

    // Preview / live mode — use MUI Drawer
    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: 280, display: 'flex', flexDirection: 'column' } }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2, borderBottom: '1px solid #f0f2f5' }}>
                <LogoArea config={config} />
                <IconButton size="small" onClick={onClose} sx={{ color: '#5a6474' }}>
                    <CloseOutlined sx={{ fontSize: 20 }} />
                </IconButton>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {links.length === 0 ? (
                    <Typography sx={{ fontSize: '0.8rem', color: '#c0c8d4', fontStyle: 'italic' }}>No nav links added</Typography>
                ) : (
                    links.map((link, i) => (
                        <NavLink key={link.id || i} link={link} color={config.style?.linkColor} onExternalConfirm={onExternalConfirm} mobile />
                    ))
                )}
            </Box>

            <Divider />
            <Box sx={{ px: 2.5, py: 2.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: config.style?.loginColor || PRIMARY, cursor: 'pointer', textAlign: 'center' }}>
                    {config.loginLabel || 'Login'}
                </Typography>
                <Box sx={{ py: 1.25, borderRadius: '8px', background: config.style?.registerBg || PRIMARY, cursor: 'pointer', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: config.style?.registerColor || '#fff' }}>
                        {config.registerLabel || 'Register Now'}
                    </Typography>
                </Box>
            </Box>
        </Drawer>
    );
}

// ─── Mobile header bar (2-col: logo | burger) ─────────────────────────────────
function MobileBar({ config, isBuilderMode, onExternalConfirm, pill = false }) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const bg = pill ? (config.style?.pillBg || '#ffffff') : (config.style?.background || '#ffffff');
    const borderBottom = pill ? 'none' : (config.style?.borderBottom || `1px solid ${alpha('#000', 0.08)}`);

    const bar = (
        <Box sx={{
            width: '100%',
            background: bg,
            borderBottom,
            px: '20px',
            height: pill ? 56 : 60,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            boxSizing: 'border-box',
            borderRadius: pill ? '999px' : 0,
            boxShadow: pill ? '0 4px 24px rgba(0,0,0,0.10)' : 'none',
            border: pill ? `1px solid ${alpha('#000', 0.07)}` : 'none',
            position: 'relative',
        }}>
            <LogoArea config={config} />

            {/* Burger button */}
            <Box
                onClick={e => { e.stopPropagation(); setDrawerOpen(true); }}
                sx={{
                    width: 36, height: 36, borderRadius: 1.5,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: '#5a6474',
                    border: '1px solid #e4e9f0',
                    transition: 'all 0.15s',
                    '&:hover': { background: alpha(PRIMARY, 0.06), borderColor: alpha(PRIMARY, 0.3), color: PRIMARY },
                }}
            >
                <MenuOutlined sx={{ fontSize: 20 }} />
            </Box>

            {/* Side drawer rendered relative to this bar in builder mode */}
            <MobileDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                config={config}
                onExternalConfirm={onExternalConfirm}
                isBuilderMode={isBuilderMode}
            />
        </Box>
    );

    if (!pill) return bar;

    // Pill wrapper
    return (
        <Box sx={{ width: '100%', px: '16px', pt: `${config.style?.pillTop ?? 16}px`, pb: '8px', display: 'flex', justifyContent: 'center', boxSizing: 'border-box' }}>
            {bar}
        </Box>
    );
}

// ─── Auth buttons (desktop) ───────────────────────────────────────────────────
function AuthButtons({ config, pill = false, isBuilderMode, onUpdateConfig, allPages, allForms, allLobbies, allSections, eventSlug, eventUrlMode, currentPageSlug, loginRedirectHref, onLoginRedirectChange }) {
    const showLogin   = config.showLogin !== false;
    const PRIMARY_COLOR = config.style?.registerBg || PRIMARY;

    // Register button state
    const [editing,        setEditing]        = useState(false);
    const [draft,          setDraft]           = useState(config.registerLabel || '');
    const inputRef         = useRef(null);
    const registerAnchor   = useRef(null);
    const [registerPicker, setRegisterPicker] = useState(false);

    // Login button state
    const loginAnchor      = useRef(null);
    const [loginPicker,    setLoginPicker]    = useState(false);

    useEffect(() => { setDraft(config.registerLabel || ''); }, [config.registerLabel]);
    useEffect(() => { if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); } }, [editing]);

    const commitLabel = () => {
        setEditing(false);
        const t = draft.trim();
        if (t && t !== (config.registerLabel || '')) onUpdateConfig?.({ registerLabel: t });
        else setDraft(config.registerLabel || '');
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>

            {/* ── Login button — always in builder (greyed when off); hidden in preview when off ── */}
            {(showLogin || isBuilderMode) && <Box ref={loginAnchor}
                onClick={isBuilderMode ? e => { e.stopPropagation(); setRegisterPicker(false); setLoginPicker(v => !v); } : undefined}
                sx={{
                    cursor: isBuilderMode ? 'pointer' : 'default',
                    px: 1, py: 0.5, borderRadius: 1,
                    outline: loginPicker ? `2px solid ${alpha(PRIMARY, 0.5)}` : '2px solid transparent',
                    outlineOffset: 2,
                    opacity: showLogin ? 1 : 0.35,
                    transition: 'all 0.15s',
                    '&:hover': isBuilderMode ? { background: alpha(PRIMARY, 0.06) } : {},
                }}
            >
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: config.style?.loginColor || PRIMARY, whiteSpace: 'nowrap', userSelect: 'none' }}>
                    {config.loginLabel || 'Login'}
                </Typography>
            </Box>

            }
            {/* ── Register button ── */}
            <Box ref={registerAnchor}
                sx={{ px: 2.5, py: pill ? 0.85 : 0.9, borderRadius: pill ? '999px' : '8px', background: PRIMARY_COLOR, cursor: isBuilderMode ? 'pointer' : 'default', outline: registerPicker ? `2px solid ${alpha(PRIMARY, 0.6)}` : '2px solid transparent', outlineOffset: 2, transition: 'all 0.15s', '&:hover': isBuilderMode ? { filter: 'brightness(0.92)' } : {} }}
                onClick={isBuilderMode ? e => { e.stopPropagation(); if (!editing) { setLoginPicker(false); setRegisterPicker(v => !v); } } : undefined}
            >
                {isBuilderMode && editing ? (
                    <Box component="input" ref={inputRef} value={draft} onChange={e => setDraft(e.target.value)}
                        onBlur={commitLabel}
                        onKeyDown={e => { if (e.key === 'Enter') commitLabel(); if (e.key === 'Escape') { setDraft(config.registerLabel || ''); setEditing(false); } e.stopPropagation(); }}
                        onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}
                        sx={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '0.875rem', fontWeight: 700, fontFamily: 'inherit', color: config.style?.registerColor || '#fff', width: `${Math.max(draft.length, 6)}ch`, minWidth: 60, p: 0, cursor: 'text' }} />
                ) : (
                    <Typography
                        onDoubleClick={isBuilderMode ? e => { e.stopPropagation(); setRegisterPicker(false); setEditing(true); } : undefined}
                        title={isBuilderMode ? 'Click to set link · Double-click to rename' : undefined}
                        sx={{ fontSize: '0.875rem', fontWeight: 700, color: config.style?.registerColor || '#fff', whiteSpace: 'nowrap', userSelect: isBuilderMode ? 'none' : 'auto' }}>
                        {config.registerLabel || 'Register Now'}
                    </Typography>
                )}
            </Box>

            {/* Login picker — owns the Show Login toggle */}
            {loginPicker && isBuilderMode && (
                <PageLinkPickerPopup
                    anchorEl={loginAnchor.current}
                    allPages={allPages} allForms={allForms} allLobbies={allLobbies} allSections={allSections}
                    currentHref={loginRedirectHref || ''}
                    eventSlug={eventSlug} eventUrlMode={eventUrlMode} currentPageSlug={currentPageSlug}
                    isLoginPicker={true}
                    showLogin={showLogin}
                    onToggleLogin={v => onUpdateConfig?.({ showLogin: v })}
                    onSelect={item => { onLoginRedirectChange?.(item.href); setLoginPicker(false); }}
                    onSelectExternal={href => { onLoginRedirectChange?.(href); setLoginPicker(false); }}
                    onClose={() => setLoginPicker(false)}
                />
            )}

            {/* Register picker — no toggle */}
            {registerPicker && isBuilderMode && (
                <PageLinkPickerPopup
                    anchorEl={registerAnchor.current}
                    allPages={allPages} allForms={allForms} allLobbies={allLobbies} allSections={allSections}
                    currentHref={config.registerHref} eventSlug={eventSlug} eventUrlMode={eventUrlMode} currentPageSlug={currentPageSlug}
                    onSelect={item => {
                        onUpdateConfig?.({ registerHref: item.href, registerIsRegistration: !!item.isRegistration });
                        setRegisterPicker(false);
                    }}
                    onSelectExternal={href => {
                        onUpdateConfig?.({ registerHref: href });
                        setRegisterPicker(false);
                    }}
                    onClose={() => setRegisterPicker(false)}
                />
            )}
        </Box>
    );
}

// ─── Desktop: Classic full-width bar ─────────────────────────────────────────
function ClassicHeader({ config, onExternalConfirm, isBuilderMode, onUpdateConfig, allPages, allForms, allLobbies, allSections, eventSlug, eventUrlMode, currentPageSlug, loginRedirectHref, onLoginRedirectChange }) {
    const links = config.links || [];
    const [logoAnchor, setLogoAnchor] = useState(null);
    const logoRef = useRef(null);

    return (
        <Box sx={{
            width: '100%',
            background: config.style?.background || '#ffffff',
            borderBottom: config.style?.borderBottom || `1px solid ${alpha('#000', 0.08)}`,
            px: config.style?.paddingX || '48px',
            height: config.style?.height || 72,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 2, boxSizing: 'border-box',
        }}>
            <Box ref={logoRef}>
                <LogoArea config={config} onLogoClick={isBuilderMode ? (e => { e.stopPropagation(); setLogoAnchor(logoRef.current); }) : undefined} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1, justifyContent: 'center' }}>
                {isBuilderMode
                    ? <HeaderEditableNavLinksArea config={config} allPages={allPages} allForms={allForms} allLobbies={allLobbies} allSections={allSections}
                        eventSlug={eventSlug} eventUrlMode={eventUrlMode} currentPageSlug={currentPageSlug} onChange={newLinks => onUpdateConfig({ links: newLinks })} />
                    : links.length === 0
                        ? <Typography sx={{ fontSize: '0.78rem', color: '#c0c8d4', fontStyle: 'italic' }}>No nav links added</Typography>
                        : links.map((link, i) => <NavLink key={link.id || i} link={link} color={config.style?.linkColor} onExternalConfirm={onExternalConfirm} />)
                }
            </Box>
            <AuthButtons config={config} pill={false} isBuilderMode={isBuilderMode} onUpdateConfig={onUpdateConfig} allPages={allPages} allForms={allForms} allLobbies={allLobbies} allSections={allSections} eventSlug={eventSlug} eventUrlMode={eventUrlMode} currentPageSlug={currentPageSlug} loginRedirectHref={loginRedirectHref} onLoginRedirectChange={onLoginRedirectChange} />

            {logoAnchor && isBuilderMode && (
                <HeaderLogoPopup anchorEl={logoAnchor} config={config} onChangeConfig={onUpdateConfig} onClose={() => setLogoAnchor(null)} />
            )}
        </Box>
    );
}

// ─── Desktop: Pill header ─────────────────────────────────────────────────────
function PillHeader({ config, onExternalConfirm, isBuilderMode, onUpdateConfig, allPages, allForms, allLobbies, allSections, eventSlug, eventUrlMode, currentPageSlug, loginRedirectHref, onLoginRedirectChange }) {
    const links = config.links || [];
    const paddingX  = config.style?.paddingX   || '40px';
    const maxWidth  = config.style?.pillMaxWidth || 1100;
    const topOffset = config.style?.pillTop      ?? 16;
    const [logoAnchor, setLogoAnchor] = useState(null);
    const logoRef = useRef(null);

    return (
        <Box sx={{ width: '100%', px: paddingX, pt: `${topOffset + 8}px`, pb: `${topOffset + 8}px`, display: 'flex', justifyContent: 'center', boxSizing: 'border-box' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: config.style?.pillBg || '#ffffff', borderRadius: '999px', boxShadow: '0 4px 24px rgba(0,0,0,0.10)', px: 2.5, height: 56, gap: 2, width: '100%', maxWidth, boxSizing: 'border-box', border: `1px solid ${alpha('#000', 0.07)}` }}>
                <Box ref={logoRef}>
                    <LogoArea config={config} onLogoClick={isBuilderMode ? (e => { e.stopPropagation(); setLogoAnchor(logoRef.current); }) : undefined} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flex: 1, justifyContent: 'center' }}>
                    {isBuilderMode
                        ? <HeaderEditableNavLinksArea config={config} allPages={allPages} allForms={allForms} allLobbies={allLobbies} allSections={allSections}
                            eventSlug={eventSlug} eventUrlMode={eventUrlMode} currentPageSlug={currentPageSlug} onChange={newLinks => onUpdateConfig({ links: newLinks })} />
                        : links.length === 0
                            ? <Typography sx={{ fontSize: '0.73rem', color: '#c0c8d4', fontStyle: 'italic' }}>No nav links added</Typography>
                            : links.map((link, i) => <NavLink key={link.id || i} link={link} color={config.style?.linkColor} onExternalConfirm={onExternalConfirm} />)
                    }
                </Box>
                <AuthButtons config={config} pill={true} isBuilderMode={isBuilderMode} onUpdateConfig={onUpdateConfig} allPages={allPages} allForms={allForms} allLobbies={allLobbies} allSections={allSections} eventSlug={eventSlug} eventUrlMode={eventUrlMode} currentPageSlug={currentPageSlug} loginRedirectHref={loginRedirectHref} onLoginRedirectChange={onLoginRedirectChange} />
                {logoAnchor && isBuilderMode && (
                    <HeaderLogoPopup anchorEl={logoAnchor} config={config} onChangeConfig={onUpdateConfig} onClose={() => setLogoAnchor(null)} />
                )}
            </Box>
        </Box>
    );
}

// ─── Chip label (top-left inside selection border) ────────────────────────────
function ChipLabel({ selected, pill, mobile }) {
    const text = [pill ? 'Header · Pill' : 'Header', mobile ? '· Mobile' : ''].filter(Boolean).join(' ');
    return (
        <Box sx={{
            position: 'absolute', top: 2, left: 2, zIndex: 20,
            px: 0.9, py: 0.2,
            background: selected ? PRIMARY : alpha(PRIMARY, 0.12),
            borderRadius: '4px 0 4px 0',
            fontSize: '0.58rem', fontWeight: 700,
            color: selected ? '#fff' : PRIMARY,
            letterSpacing: '0.5px', textTransform: 'uppercase',
            pointerEvents: 'none', transition: 'all 0.15s',
        }}>
            {text}
        </Box>
    );
}

// ─── Main export ──────────────────────────────────────────────────────────────
export function HeaderSection({ section, config: configProp, selected, onSelectHeader, onUpdateConfig, allPages = [], allForms = [], allLobbies = [], allSections = [], eventSlug = '', eventUrlMode = 'prefixed', currentPageSlug = '', breakpoint = 'desktop', loginRedirectHref = null, onLoginRedirectChange }) {
  const isPreview = useContext(PreviewCtx);
  const config = configProp || section.headerConfig || {};
    const isPill    = (config.template || 'classic') === 'pill';
    const isSticky  = config.sticky === true;
    const isMobile  = breakpoint === 'mobile';
    const isBuilderMode = !isPreview;

    const [externalDialog, setExternalDialog] = useState(null);
    const handleExternalConfirm = (url, newTab) => setExternalDialog({ url, newTab });
    const confirmVisit = () => {
        if (externalDialog?.url) window.open(externalDialog.url, externalDialog.newTab ? '_blank' : '_self', 'noopener,noreferrer');
        setExternalDialog(null);
    };

    const sharedProps = { config, onExternalConfirm: handleExternalConfirm, isBuilderMode, onUpdateConfig, allPages, allForms, allLobbies, allSections, eventSlug, eventUrlMode, currentPageSlug, loginRedirectHref, onLoginRedirectChange };

    const inner = isMobile
        ? <MobileBar config={config} isBuilderMode={isBuilderMode} onExternalConfirm={handleExternalConfirm} pill={isPill} />
        : isPill
            ? <PillHeader    {...sharedProps} />
            : <ClassicHeader {...sharedProps} />;

    const stickySpacerHeight = (() => {
        if (isPill) {
            const topOffset = config.style?.pillTop ?? 16;
            return topOffset + 56 + 8;
        }
        return config.style?.height || 72;
    })();

    // ── Preview mode ───────────────────────────────────────────────────────────
    if (isPreview) {
        const wrapSx = isPill
            ? { position: isSticky ? 'sticky' : 'relative', top: 0, height: 0, overflow: 'visible', mb: '-65px', zIndex: isSticky ? 200 : 10 }
            : { position: isSticky ? 'sticky' : 'relative', top: 0, zIndex: isSticky ? 200 : 'auto' };

        return (
            <>
                <Box sx={wrapSx}>
                    {isPill
                        ? <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0 }}>{inner}</Box>
                        : inner
                    }
                </Box>
                <Box sx={{ height: stickySpacerHeight, flexShrink: 0, pointerEvents: 'none' }} />
                <ExternalLinkDialog open={!!externalDialog} url={externalDialog?.url || ''} onConfirm={confirmVisit} onCancel={() => setExternalDialog(null)} />
            </>
        );
    }

    // ── Builder mode ───────────────────────────────────────────────────────────
    const selectionSx = {
        outline: selected ? `2px solid ${PRIMARY}` : '2px solid transparent',
        outlineOffset: -2,
        transition: 'outline-color 0.15s',
        '&:hover': { outline: `2px solid ${selected ? PRIMARY : alpha(PRIMARY, 0.45)}` },
    };

    const onClick = (e) => { e.stopPropagation(); onSelectHeader(section.id); };

    if (isPill) {
        return (
            <>
                <Box onClick={onClick} sx={{
                    position: isSticky ? 'sticky' : 'relative',
                    top: 0, height: 0, overflow: 'visible',
                    mb: '-65px',
                    zIndex: isSticky ? 200 : 10, cursor: 'pointer',
                }}>
                    <Box sx={{
                        position: 'absolute', top: 0, left: 0, right: 0,
                        ...selectionSx, cursor: 'pointer', userSelect: 'none',
                    }}>
                        {inner}
                        <ChipLabel selected={selected} pill mobile={isMobile} />
                    </Box>
                </Box>
                <Box sx={{ height: stickySpacerHeight, flexShrink: 0, pointerEvents: 'none' }} />
                <ExternalLinkDialog open={!!externalDialog} url={externalDialog?.url || ''} onConfirm={confirmVisit} onCancel={() => setExternalDialog(null)} />
            </>
        );
    }

    return (
        <>
            <Box onClick={onClick} sx={{
                position: isSticky ? 'sticky' : 'relative',
                top: 0, zIndex: isSticky ? 200 : 'auto',
                cursor: 'pointer', userSelect: 'none',
                ...selectionSx,
            }}>
                {inner}
                <ChipLabel selected={selected} pill={false} mobile={isMobile} />
            </Box>
            <ExternalLinkDialog open={!!externalDialog} url={externalDialog?.url || ''} onConfirm={confirmVisit} onCancel={() => setExternalDialog(null)} />
        </>
    );
}